import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

const OFFLINE_DIR = 'offline-lessons';

async function ensureDir() {
  try {
    await Filesystem.mkdir({ path: OFFLINE_DIR, directory: Directory.Data, recursive: true });
  } catch (err) {
    // Already exists — Filesystem throws if the directory is there.
  }
}

function lessonPath(lessonId) {
  return `${OFFLINE_DIR}/${lessonId}.json`;
}

/** Fetches a same-origin file and inlines it as a base64 data string, for offline storage. */
async function fetchAsBase64(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Saves a lesson's content, plus its attachments, to on-device storage so it
 * can be read without a network connection. Native platforms only.
 */
export async function saveLessonOffline(lesson, attachments = [], moduleId = null) {
  if (!Capacitor.isNativePlatform()) return;
  await ensureDir();

  const attachmentData = [];
  for (const att of attachments) {
    try {
      attachmentData.push({
        id: att.id,
        title: att.title,
        description: att.description,
        dataUrl: await fetchAsBase64(att.file),
      });
    } catch (err) {
      console.warn(`Failed to cache attachment ${att.id} for offline use`, err);
    }
  }

  const payload = {
    lesson,
    moduleId,
    attachments: attachmentData,
    savedAt: new Date().toISOString(),
  };

  await Filesystem.writeFile({
    path: lessonPath(lesson.id),
    directory: Directory.Data,
    data: JSON.stringify(payload),
    encoding: Encoding.UTF8,
  });
}

export async function getOfflineLesson(lessonId) {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const result = await Filesystem.readFile({
      path: lessonPath(lessonId),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    return JSON.parse(result.data);
  } catch (err) {
    return null;
  }
}

export async function isLessonAvailableOffline(lessonId) {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await Filesystem.stat({ path: lessonPath(lessonId), directory: Directory.Data });
    return true;
  } catch (err) {
    return false;
  }
}

export async function deleteOfflineLesson(lessonId) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Filesystem.deleteFile({ path: lessonPath(lessonId), directory: Directory.Data });
  } catch (err) {
    // Already gone — nothing to do.
  }
}

/** Lists every lesson currently saved for offline reading (id + cached metadata). */
export async function listOfflineLessons() {
  if (!Capacitor.isNativePlatform()) return [];
  await ensureDir();
  try {
    const { files } = await Filesystem.readdir({ path: OFFLINE_DIR, directory: Directory.Data });
    const lessons = [];
    for (const file of files) {
      const name = typeof file === 'string' ? file : file.name;
      if (!name.endsWith('.json')) continue;
      const lessonId = name.replace('.json', '');
      const cached = await getOfflineLesson(lessonId);
      if (cached) lessons.push(cached);
    }
    return lessons;
  } catch (err) {
    return [];
  }
}
